import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Drawer,
  Tooltip,
  Typography,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert
} from '@mui/material';
import {
  SmartToy,
  Folder,
  Event,
  ChevronRight,
  ChevronLeft
} from '@mui/icons-material';
import ProjectsSidebar from '../components/projects/ProjectsSidebar';
import ChatPanel from '../components/chat/ChatPanel';
import MeetingsSidebar from '../components/meetings/MeetingsSidebar';
import { mockProjects } from '../data/mockData';
import { Project, Meeting } from '../types';
import { useAuth } from '../context/AuthContext';
import { fetchMeetingsFromApi, mapApiMeetingToMeeting, formatNameFromEmail } from '../services/meetingService';
import { getMyJiraProjects } from '../services/jiraService';

type MobileTabType = 'chat' | 'projects' | 'meetings';

const HomePage: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // 900px - 1199px
  const { firebaseUser } = useAuth();

  const [projectsList, setProjectsList] = useState<Project[]>(mockProjects);
  const [selectedProject, setSelectedProject] = useState<Project>(mockProjects[1]); // Default to Mobile App
  const [meetingsList, setMeetingsList] = useState<Meeting[]>([]);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [isLoadingMeetings, setIsLoadingMeetings] = useState(false);

  // Mobile navigation tabs & drawer overlays
  const [mobileTab, setMobileTab] = useState<MobileTabType>('chat');
  const [projectsDrawerOpen, setProjectsDrawerOpen] = useState(false);
  const [meetingsDrawerOpen, setMeetingsDrawerOpen] = useState(false);

  // Desktop collapsible sidebars
  const [leftSidebarOpen, setLeftSidebarOpen] = useState(true);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(!isTablet);

  // Toast notification on project/meeting switch
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Handle participant added via Share meeting dialog
  const handleParticipantAdded = (meetingId: string, email: string) => {
    const newName = formatNameFromEmail(email);
    const newParticipant = {
      id: `p-${Date.now()}-${email}`,
      name: newName,
      email: email,
      avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(newName)}&background=FF6577&color=fff`
    };

    setMeetingsList((prev) =>
      prev.map((m) => {
        if (m.id === meetingId || m.meeting_id === meetingId) {
          const currentParts = m.participants || [];
          if (!currentParts.some((p) => p.email === email)) {
            return {
              ...m,
              participants: [...currentParts, newParticipant]
            };
          }
        }
        return m;
      })
    );

    setSelectedMeeting((prev) => {
      if (prev && (prev.id === meetingId || prev.meeting_id === meetingId)) {
        const currentParts = prev.participants || [];
        if (!currentParts.some((p) => p.email === email)) {
          return {
            ...prev,
            participants: [...currentParts, newParticipant]
          };
        }
      }
      return prev;
    });

    setToastMessage(`Meeting shared with ${email}`);
  };

  // Fetch real meetings from Cloud Run REST API
  const loadMeetings = useCallback(async (showToast = false) => {
    setIsLoadingMeetings(true);
    try {
      const apiMeetings = await fetchMeetingsFromApi();
      if (apiMeetings && apiMeetings.length > 0) {
        const mapped = apiMeetings.map((m, idx) => mapApiMeetingToMeeting(m, idx));
        setMeetingsList(mapped);
        setSelectedMeeting(mapped[0]);
        if (showToast) {
          setToastMessage(`Synced ${mapped.length} meeting(s) from Cloud Run`);
        }
      } else {
        setMeetingsList([]);
        setSelectedMeeting(null);
        if (showToast) {
          setToastMessage('Cloud Run API returned 0 meetings for this account.');
        }
      }
    } catch (err: any) {
      console.warn('Cloud Run API fetch failed:', err.message);
      if (showToast) {
        setToastMessage(`Cloud Run: ${err.message}`);
      }
    } finally {
      setIsLoadingMeetings(false);
    }
  }, []);

  // Fetch accessible Jira projects from Cloud Run API
  const loadJiraProjects = useCallback(async () => {
    try {
      const jiraProjects = await getMyJiraProjects();
      if (jiraProjects && jiraProjects.length > 0) {
        const defaultColors = ['#4F6BFF', '#22C99A', '#FF8800', '#FF6577', '#8E44AD', '#3498DB'];
        const mapped: Project[] = jiraProjects.map((jp, idx) => ({
          id: jp.id || jp.key,
          name: jp.name,
          code: jp.key,
          color: defaultColors[idx % defaultColors.length],
          epicCount: jp.epicCount ?? 3,
          storyCount: jp.storyCount ?? 12,
          healthScore: 92,
          onTrackPct: 85,
          atRiskPct: 10,
          blockedPct: 5
        }));
        setProjectsList(mapped);
        setSelectedProject((prev) => {
          const match = mapped.find((p) => p.code === prev.code || p.id === prev.id);
          return match || mapped[0];
        });
      }
    } catch (err: any) {
      console.warn('Jira projects sync:', err.message);
    }
  }, []);

  useEffect(() => {
    loadMeetings(false);
    loadJiraProjects();
  }, [loadMeetings, loadJiraProjects, firebaseUser]);

  const handleSelectProject = (project: Project) => {
    setSelectedProject(project);
    setToastMessage(`Switched active context to ${project.name}`);
    if (projectsDrawerOpen) {
      setProjectsDrawerOpen(false);
    }
  };

  const handleSelectMeeting = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    setToastMessage(`Switched active meeting to ${meeting.title}`);
    if (meetingsDrawerOpen) {
      setMeetingsDrawerOpen(false);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        p: { xs: 1, sm: 1.5, md: 2 },
        gap: { xs: 1, sm: 1.5, md: 2 },
        bgcolor: 'var(--hp-bg)',
        overflow: 'hidden',
        boxSizing: 'border-box'
      }}
    >
      {/* Mobile Segmented Navigation Tabs (< 900px) */}
      {isMobile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 0.6,
            bgcolor: 'var(--hp-surface)',
            borderRadius: '14px',
            border: '1px solid var(--hp-border)',
            boxShadow: 'inset 2px 2px 5px #E0E7F0, inset -2px -2px 5px #FFFFFF',
            flexShrink: 0,
            gap: 0.5
          }}
        >
          <Button
            fullWidth
            size="small"
            startIcon={<SmartToy sx={{ fontSize: 18 }} />}
            onClick={() => setMobileTab('chat')}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: mobileTab === 'chat' ? 700 : 500,
              fontSize: '0.84rem',
              py: 0.8,
              color: mobileTab === 'chat' ? 'var(--hp-primary)' : 'var(--hp-text-secondary)',
              bgcolor: mobileTab === 'chat' ? 'var(--hp-surface-raised)' : 'transparent',
              boxShadow: mobileTab === 'chat' ? '2px 2px 6px var(--hp-shadow-dark)' : 'none',
              transition: 'all 0.18s ease'
            }}
          >
            AI Assistant
          </Button>

          <Button
            fullWidth
            size="small"
            startIcon={<Folder sx={{ fontSize: 18 }} />}
            onClick={() => setMobileTab('projects')}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: mobileTab === 'projects' ? 700 : 500,
              fontSize: '0.84rem',
              py: 0.8,
              color: mobileTab === 'projects' ? 'var(--hp-primary)' : 'var(--hp-text-secondary)',
              bgcolor: mobileTab === 'projects' ? 'var(--hp-surface-raised)' : 'transparent',
              boxShadow: mobileTab === 'projects' ? '2px 2px 6px var(--hp-shadow-dark)' : 'none',
              transition: 'all 0.18s ease'
            }}
          >
            Projects
          </Button>

          <Button
            fullWidth
            size="small"
            startIcon={<Event sx={{ fontSize: 18 }} />}
            onClick={() => setMobileTab('meetings')}
            sx={{
              borderRadius: '10px',
              textTransform: 'none',
              fontWeight: mobileTab === 'meetings' ? 700 : 500,
              fontSize: '0.84rem',
              py: 0.8,
              color: mobileTab === 'meetings' ? 'var(--hp-primary)' : 'var(--hp-text-secondary)',
              bgcolor: mobileTab === 'meetings' ? 'var(--hp-surface-raised)' : 'transparent',
              boxShadow: mobileTab === 'meetings' ? '2px 2px 6px var(--hp-shadow-dark)' : 'none',
              transition: 'all 0.18s ease'
            }}
          >
            Meetings
          </Button>
        </Box>
      )}

      {/* Main Workspace Layout Area */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, width: '100%', gap: { xs: 1, md: 1.5, lg: 2 } }}>
        {/* ======================================================== */}
        {/* MOBILE VIEW HANDLING (< 900px) */}
        {/* ======================================================== */}
        {isMobile ? (
          <Box sx={{ width: '100%', height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {mobileTab === 'chat' && (
              <ChatPanel
                selectedProject={selectedProject}
                selectedMeeting={selectedMeeting}
                onOpenProjectsDrawer={() => setProjectsDrawerOpen(true)}
                onOpenMeetingsDrawer={() => setMeetingsDrawerOpen(true)}
                isMobile={true}
              />
            )}

            {mobileTab === 'projects' && (
              <Box className="neumorphic-card" sx={{ height: '100%', overflowY: 'auto' }}>
                <ProjectsSidebar
                  projects={projectsList}
                  selectedProject={selectedProject}
                  onSelectProject={handleSelectProject}
                  onOpenInChat={() => setMobileTab('chat')}
                  isMobile={true}
                />
              </Box>
            )}

            {mobileTab === 'meetings' && (
              <Box className="neumorphic-card" sx={{ height: '100%', overflowY: 'auto' }}>
                <MeetingsSidebar
                  meetings={meetingsList}
                  selectedMeeting={selectedMeeting}
                  onSelectMeeting={handleSelectMeeting}
                  onOpenInChat={() => setMobileTab('chat')}
                  onRefreshMeetings={() => loadMeetings(true)}
                  isLoadingMeetings={isLoadingMeetings}
                  isMobile={true}
                  onParticipantAdded={handleParticipantAdded}
                />
              </Box>
            )}
          </Box>
        ) : (
          /* ======================================================== */
          /* DESKTOP & TABLET VIEW HANDLING (>= 900px) */
          /* ======================================================== */
          <>
            {/* Left Column: Projects Sidebar */}
            {leftSidebarOpen ? (
              <Box
                sx={{
                  width: { md: 280, lg: 320, xl: 350 },
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: 0,
                  transition: 'width 0.25s ease'
                }}
              >
                <ProjectsSidebar
                  projects={projectsList}
                  selectedProject={selectedProject}
                  onSelectProject={handleSelectProject}
                  onCollapse={() => setLeftSidebarOpen(false)}
                />
              </Box>
            ) : (
              /* Mini Collapsed Left Bar */
              <Tooltip title="Expand Projects (Click to open)" placement="right">
                <Box
                  onClick={() => setLeftSidebarOpen(true)}
                  sx={{
                    width: 44,
                    height: '100%',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 2,
                    cursor: 'pointer',
                    bgcolor: 'var(--hp-surface)',
                    borderRadius: '16px',
                    border: '1px solid var(--hp-border)',
                    boxShadow: '2px 2px 8px var(--hp-shadow-dark), -2px -2px 8px var(--hp-shadow-light)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'var(--hp-surface-raised)',
                      borderColor: 'var(--hp-primary)'
                    }
                  }}
                >
                  <Folder sx={{ color: 'var(--hp-primary)', fontSize: 20, mb: 2 }} />
                  <Typography
                    sx={{
                      writingMode: 'vertical-rl',
                      transform: 'rotate(180deg)',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      color: 'var(--hp-text-secondary)',
                      letterSpacing: 1.5
                    }}
                  >
                    PROJECTS
                  </Typography>
                  <Box sx={{ mt: 'auto', color: 'var(--hp-text-secondary)' }}>
                    <ChevronRight fontSize="small" />
                  </Box>
                </Box>
              </Tooltip>
            )}

            {/* Center Column: Chat Assistant */}
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', height: '100%' }}>
              <ChatPanel
                selectedProject={selectedProject}
                selectedMeeting={selectedMeeting}
                leftSidebarOpen={leftSidebarOpen}
                rightSidebarOpen={rightSidebarOpen}
                onToggleLeftSidebar={() => setLeftSidebarOpen(!leftSidebarOpen)}
                onToggleRightSidebar={() => setRightSidebarOpen(!rightSidebarOpen)}
                onOpenProjectsDrawer={() => setLeftSidebarOpen(true)}
                onOpenMeetingsDrawer={() => setRightSidebarOpen(true)}
                isMobile={false}
              />
            </Box>

            {/* Right Column: Meetings Sidebar */}
            {rightSidebarOpen ? (
              <Box
                sx={{
                  width: { md: 280, lg: 320, xl: 350 },
                  flexShrink: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: 0,
                  transition: 'width 0.25s ease'
                }}
              >
                <MeetingsSidebar
                  meetings={meetingsList}
                  selectedMeeting={selectedMeeting}
                  onSelectMeeting={handleSelectMeeting}
                  onCollapse={() => setRightSidebarOpen(false)}
                  onRefreshMeetings={() => loadMeetings(true)}
                  isLoadingMeetings={isLoadingMeetings}
                  onParticipantAdded={handleParticipantAdded}
                />
              </Box>
            ) : (
              /* Mini Collapsed Right Bar */
              <Tooltip title="Expand Meetings (Click to open)" placement="left">
                <Box
                  onClick={() => setRightSidebarOpen(true)}
                  sx={{
                    width: 44,
                    height: '100%',
                    flexShrink: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    py: 2,
                    cursor: 'pointer',
                    bgcolor: 'var(--hp-surface)',
                    borderRadius: '16px',
                    border: '1px solid var(--hp-border)',
                    boxShadow: '2px 2px 8px var(--hp-shadow-dark), -2px -2px 8px var(--hp-shadow-light)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'var(--hp-surface-raised)',
                      borderColor: 'var(--hp-primary)'
                    }
                  }}
                >
                  <Event sx={{ color: 'var(--hp-primary)', fontSize: 20, mb: 2 }} />
                  <Typography
                    sx={{
                      writingMode: 'vertical-rl',
                      transform: 'rotate(180deg)',
                      fontSize: '0.78rem',
                      fontWeight: 700,
                      color: 'var(--hp-text-secondary)',
                      letterSpacing: 1.5
                    }}
                  >
                    MEETINGS
                  </Typography>
                  <Box sx={{ mt: 'auto', color: 'var(--hp-text-secondary)' }}>
                    <ChevronLeft fontSize="small" />
                  </Box>
                </Box>
              </Tooltip>
            )}
          </>
        )}
      </Box>

      {/* Mobile Drawer Overlay for Projects */}
      <Drawer
        anchor="left"
        open={projectsDrawerOpen}
        onClose={() => setProjectsDrawerOpen(false)}
        slotProps={{
          paper: {
            sx: {
              width: '85vw',
              maxWidth: 360,
              p: 2,
              bgcolor: 'var(--hp-bg)',
              boxShadow: '6px 0 24px rgba(0,0,0,0.1)'
            }
          }
        }}
      >
        <ProjectsSidebar
          projects={projectsList}
          selectedProject={selectedProject}
          onSelectProject={handleSelectProject}
          onClose={() => setProjectsDrawerOpen(false)}
          onOpenInChat={() => setProjectsDrawerOpen(false)}
          isMobile={true}
        />
      </Drawer>

      {/* Mobile Drawer Overlay for Meetings */}
      <Drawer
        anchor="right"
        open={meetingsDrawerOpen}
        onClose={() => setMeetingsDrawerOpen(false)}
        slotProps={{
          paper: {
            sx: {
              width: '88vw',
              maxWidth: 380,
              p: 2,
              bgcolor: 'var(--hp-bg)',
              boxShadow: '-6px 0 24px rgba(0,0,0,0.1)'
            }
          }
        }}
      >
        <MeetingsSidebar
          meetings={meetingsList}
          selectedMeeting={selectedMeeting}
          onSelectMeeting={handleSelectMeeting}
          onClose={() => setMeetingsDrawerOpen(false)}
          onOpenInChat={() => setMeetingsDrawerOpen(false)}
          onRefreshMeetings={() => loadMeetings(true)}
          isLoadingMeetings={isLoadingMeetings}
          isMobile={true}
          onParticipantAdded={handleParticipantAdded}
        />
      </Drawer>

      {/* Context Switch Toast Notification */}
      <Snackbar
        open={Boolean(toastMessage)}
        autoHideDuration={3000}
        onClose={() => setToastMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setToastMessage(null)}
          severity="info"
          sx={{
            borderRadius: '12px',
            bgcolor: 'var(--hp-surface-raised)',
            color: 'var(--hp-text)',
            border: '1px solid var(--hp-border)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
          }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HomePage;
